// @ts-ignore
import React, { FC, useState, useEffect, useMemo } from 'react';
import { BemMethod, createBem, PlatformTypes } from '@om-fe/utils';
import { MatchListSkeleton } from '@om-fe/widgets/dist/widgets/match-list/match-list-skeleton';
import { useI18NContext } from '@om-fe/core/core/i18n/useI18ncontext';
import { DisciplineRouteType } from '@om-fe/core/components/discipline-anchor/discipline-route.utils';
import { UseSimpleStore, useSimpleStoreList, useTopicStoreReady } from '@om-fe/store';
import { MixedGroupedMatchListItem } from './mixed-grouped-match-list-item';
import { MixedGroupedTournamentModel } from '../../mixed-grouped.types';
import getConfig, { OMFE2ConfigModel } from '@om-fe/core/utils/config';
import { MAIN_MARKET_MIN_LIMIT } from '@om-fe/core/components/match-list/utils/match-list.constants';
import { DisciplineLiveStatus } from '@om-fe/models';
import { RpcStateStatus, useRpcState } from '@om-fe/widgets/dist/common/rpc/rpc-state.hooks';
import { GetMarketGroupsResponse, GetMarketGroupsOptions } from '@om-fe/api';
import { FilteredMarketGroupsOverview } from '../../../match-list/utils/match-list-models';
import { MarketGroupOverviewModel } from '@om-fe/models';
import TournamentMatchListSelector from '../../../match-list/containers/tournament-match-list/tournament-match-list-selector';
import { DEFAULT_LIMIT_NUMBER_OF_EVENTS } from '../../../../common/constants';
import keyBy from 'lodash/keyBy';

export interface MixedGroupedMatchListProps {
    readonly useStore: UseSimpleStore<MixedGroupedTournamentModel>;
    readonly sportPageType: DisciplineRouteType;
    readonly mainMarketsLimit?: number;
    readonly disciplineId?: string;
    readonly deviceType?: PlatformTypes;
}

const bem: BemMethod = createBem('MixedGroupedMatchList');

export const MixedGroupedMatchList: FC<MixedGroupedMatchListProps> = ((
    {
        useStore,
        sportPageType,
        mainMarketsLimit,
        deviceType,
        disciplineId,
    }: MixedGroupedMatchListProps,
) => {
    const ready: boolean = useTopicStoreReady(useStore);
    const list: string[] = useSimpleStoreList(useStore);

    const { gettext } = useI18NContext();
    const config: OMFE2ConfigModel = getConfig();
    const marketSelector: boolean = config?.matchListConfig?.matchListSelector?.enabled ?? false;
    const showMarketSelectorHeader: boolean = config.matchListConfig?.matchListSelector?.showHeader ?? true;
    const marketGroupsOverviewNumber: number | undefined = config?.matchListConfig?.matchListSelector?.numberOfList;

    const [groupOverview, setGroupOverview] = useState('');
    const [isLoading, setIsLoading] = useState(marketSelector);

    const [filteredMarketGroupsOverview, setFilteredMarketGroupsOverview] = useState<Record<string, FilteredMarketGroupsOverview>>();
    const [filteredMarketGroupsOverviewList, setFilteredMarketGroupsOverviewList] = useState<string[]>([]);
    const params: GetMarketGroupsOptions = useMemo(() => ({ sportId: disciplineId!, liveStatus: DisciplineLiveStatus.BOTH }), [disciplineId]);
    const [rpcResponse] = useRpcState<GetMarketGroupsResponse>('marketGroupsOverview', params);

    useEffect(()=>{
        if (rpcResponse.status === RpcStateStatus.Success) {
            const { data } = rpcResponse;
            const marketGroupsOverviewData: MarketGroupOverviewModel[] | undefined = data.response;
            const filteredMarketGroupsOverviewData: Record<string, FilteredMarketGroupsOverview> = keyBy(marketGroupsOverviewData, 'id');
            const filteredMarketGroupsOverviewListData = marketGroupsOverviewData.map(item => item.id);
            const groupOverviewData = filteredMarketGroupsOverviewListData.slice(0, mainMarketsLimit).join(',');
            setFilteredMarketGroupsOverview(filteredMarketGroupsOverviewData);
            setFilteredMarketGroupsOverviewList(filteredMarketGroupsOverviewListData);
            setGroupOverview(groupOverviewData);
            setIsLoading(false);
        }
    },[rpcResponse, mainMarketsLimit]);

    if (
        rpcResponse.status === RpcStateStatus.Loading ||
        rpcResponse.status === RpcStateStatus.Initial ||
        rpcResponse.status === RpcStateStatus.Failed ||
        typeof groupOverview === 'undefined' ||
        !ready) {
        return <MatchListSkeleton />;
    }

    const selectGroupsOverview = (item: string) => {
        const limit = mainMarketsLimit ?? MAIN_MARKET_MIN_LIMIT;
        const listSelected = limit === 1
            ? item
            : filteredMarketGroupsOverviewList?.slice(0, limit - 1)
                .concat(item)
                .join(',');
            setGroupOverview(listSelected);
    }

    if (!list.length) {
        return (
            <div className={bem({ extra: 'EmptyList' })}>{gettext('No matches currently available')}</div>
        );
    }


    return (
        <div className={bem()}>
            { marketSelector && disciplineId && !showMarketSelectorHeader && <TournamentMatchListSelector
                instanceId={`mixed-grouped-match-list-item-${list[0]}-Live`}
                tournamentId={list[0]}
                disciplineId={disciplineId}
                liveStatus={DisciplineLiveStatus.BOTH}
                limitNumberOfEvents={DEFAULT_LIMIT_NUMBER_OF_EVENTS}
                mainMarketsLimit={mainMarketsLimit ?? MAIN_MARKET_MIN_LIMIT}
                marketGroupsOverviewNumber={marketGroupsOverviewNumber}
                hasMarketSelector={marketSelector}
                groupOverview={groupOverview}
                setGroupOverview={setGroupOverview}
                setLoading={setIsLoading}
                deviceType={deviceType}
            /> }
            {list.map((id: string) => (
                <MixedGroupedMatchListItem
                    useStore={useStore}
                    sportPageType={sportPageType}
                    key={id}
                    id={id}
                    mainMarketsLimit={mainMarketsLimit}
                    groupOverview={groupOverview}
                    marketSelector={marketSelector}
                    isLoading={isLoading}
                    selectGroupsOverview={selectGroupsOverview}
                    filteredMarketGroupsOverview={filteredMarketGroupsOverview}
                    filteredMarketGroupsOverviewList={filteredMarketGroupsOverviewList}
                    isHasDrawer={true}
                    deviceType={deviceType}
                    showMarketSelectorHeader={showMarketSelectorHeader}
                />
            ))}
        </div>
    );
});
